// Archive system
// When Firebase is configured, archive is stored under archive/{shiftCode}
// so it's shared across all devices that know the shift code — free on Firebase Spark plan.
// Falls back to localStorage when Firebase is not configured.

import { isFirebaseConfigured, db } from './firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const LOCAL_KEY = 'handoff_archive';

// ── localStorage fallback ──
const localLoad = () => {
  try { const r = localStorage.getItem(LOCAL_KEY); return r ? JSON.parse(r) : []; }
  catch { return []; }
};
const localSave = (records) => {
  try { localStorage.setItem(LOCAL_KEY, JSON.stringify(records)); } catch {}
};

// ── Load archive (Firebase if configured, else localStorage) ──
export const loadArchive = async (shiftCode = null) => {
  if (isFirebaseConfigured() && shiftCode && db) {
    try {
      const snap = await getDoc(doc(db, 'archive', shiftCode));
      return snap.exists() ? (snap.data().records || []) : [];
    } catch (e) {
      console.warn('Archive Firebase load failed, using local:', e);
    }
  }
  return localLoad();
};

// ── Save archive ──
export const saveArchive = async (records, shiftCode = null) => {
  if (isFirebaseConfigured() && shiftCode && db) {
    try {
      await setDoc(doc(db, 'archive', shiftCode), { records, updatedAt: new Date().toISOString() });
      return;
    } catch (e) {
      console.warn('Archive Firebase save failed, using local:', e);
    }
  }
  localSave(records);
};

// ── Move all discharged patients to archive ──
export const archiveDischarged = async (patients, shiftCode) => {
  const now = new Date().toISOString();
  const discharged = patients.filter(p => p.urgency === 'discharged');
  const remaining  = patients.filter(p => p.urgency !== 'discharged');
  if (!discharged.length) return { remaining: patients, archived: 0 };

  const existing = await loadArchive(shiftCode);
  const newRecords = discharged.map(p => ({
    id: `arc-${Date.now()}-${p.id}`,
    archivedAt: now,
    shiftCode: shiftCode || 'unknown',
    patient: p,
  }));
  await saveArchive([...existing, ...newRecords], shiftCode);
  return { remaining, archived: discharged.length };
};

// ── Manually archive a single patient ──
export const archivePatient = async (patient, shiftCode) => {
  const now = new Date().toISOString();
  const existing = await loadArchive(shiftCode);
  await saveArchive([
    ...existing,
    { id: `arc-${Date.now()}-${patient.id}`, archivedAt: now, shiftCode: shiftCode || 'manual', patient }
  ], shiftCode);
};

// ── Midnight archive gate ──
const RUN_KEY = 'handoff_archive_lastrun';
export const shouldRunMidnightArchive = () => {
  const today = new Date().toISOString().slice(0, 10);
  try { return localStorage.getItem(RUN_KEY) !== today; } catch { return false; }
};
export const markArchiveRan = () => {
  const today = new Date().toISOString().slice(0, 10);
  try { localStorage.setItem(RUN_KEY, today); } catch {}
};

// ── Clear archive ──
export const clearArchive = async (shiftCode = null) => {
  await saveArchive([], shiftCode);
  try { localStorage.removeItem(LOCAL_KEY); } catch {}
};
